import React, { useState } from 'react';
import { ArrowDownAZ, ArrowUpAZ, Clock, Archive, ArchiveRestore } from 'lucide-react';
import { ModernButton } from '../ui/ModernButton';
import { TrashIcon, SaveIcon, EraserIcon } from '../ui/Icons';
import { TemplateCombobox } from './TemplateCombobox';

export function TemplateLoadingSection({
  resetEditorToDefaults,
  selectedTemplateId,
  setSelectedTemplateId,
  loadFromTemplate,
  questionnaireTemplates,
  deleteTemplateById,
  renameTemplateById,
  toggleTemplateArchivedById,
  templateName,
  setTemplateName,
  saveAsTemplate,
  generateLink,
  selectedVersionId,
  setSelectedVersionId,
  restoreVersion,
  versions,
  lastSaveTime,
  deleteVersion,
  saveVersion,
  title,
  setTitle,
  error,
  setError,
  assignmentSection = null,
}) {
  // Open on a wide screen where it costs nothing, closed on a phone where it
  // would push the questions below the fold.
  const [toolsOpen, setToolsOpen] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches
  );
  // 'none' = original order, 'asc' = A→Z, 'desc' = Z→A — default to A→Z
  const [templateSort, setTemplateSort] = useState('asc');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const selectedTemplate = questionnaireTemplates.find(
    (t) => String(t.id) === String(selectedTemplateId)
  );
  const selectedIsArchived = !!selectedTemplate?.archived;

  const cycleTemplateSort = () =>
    setTemplateSort((s) => (s === 'none' ? 'asc' : s === 'asc' ? 'desc' : 'none'));

  const displayedTemplates = (() => {
    if (templateSort === 'none') return questionnaireTemplates;
    const sorted = [...questionnaireTemplates].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' })
    );
    return templateSort === 'desc' ? sorted.reverse() : sorted;
  })();

  const sortTitle =
    templateSort === 'asc' ? 'Sortierung: A→Z (klicken für Z→A)'
      : templateSort === 'desc' ? 'Sortierung: Z→A (klicken für Originalreihenfolge)'
        : 'Alphabetisch sortieren (klicken für A→Z)';

  const renderSortIcon = () => {
    if (templateSort === 'asc') return <ArrowDownAZ className="w-5 h-5" />;
    if (templateSort === 'desc') return <ArrowUpAZ className="w-5 h-5" />;
    // none — newest first (by date)
    return <Clock className="w-5 h-5" />;
  };

  const startRename = () => {
    if (!selectedTemplateId) return;
    const current = questionnaireTemplates.find((t) => String(t.id) === String(selectedTemplateId));
    setRenameValue(current?.name || '');
    setRenamingId(selectedTemplateId);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const confirmRename = async () => {
    const ok = await renameTemplateById?.(renamingId, renameValue);
    if (ok) {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  return (
    <>
      {/* Primary: questionnaire title + create action */}
      <div className="mb-4">
        <label htmlFor="title" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Titel des Fragebogens</label>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-stretch">
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (error) setError(null); }}
            placeholder="z.B. Fragebogen für Junior-Entwickler"
            className="flex-1 p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base"
          />
          <ModernButton
            onClick={generateLink}
            variant="success"
            size="lg"
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
          >
            Fragebogen erstellen
          </ModernButton>
        </div>
      </div>

      {/* Assignment (Zuständig) — injected by the editor */}
      {assignmentSection}

      {/* Secondary tooling: templates + version history */}
      <div className="mb-6 sm:mb-8 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setToolsOpen((open) => !open)}
          aria-expanded={toolsOpen}
          className="w-full flex flex-col items-start gap-1 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 bg-slate-50 dark:bg-slate-900 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
            <svg className={`w-4 h-4 transition-transform ${toolsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Vorlagen &amp; Versionen
          </span>
          <span className="pl-6 text-xs text-slate-400 dark:text-slate-500 sm:pl-0">
            Auto-Save alle 2 Min{lastSaveTime ? ` · Zuletzt ${new Date(lastSaveTime).toLocaleTimeString('de-DE')}` : ''}
          </span>
        </button>

        {toolsOpen && (
          <div className="p-4 space-y-5 bg-white dark:bg-slate-800">
            {/* Templates */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Vorlagen</label>
              <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex flex-col sm:flex-row items-stretch gap-2 flex-1">
                  {renamingId ? (
                    <>
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); confirmRename(); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        autoFocus
                        placeholder="Neuer Vorlagenname"
                        className="flex-1 p-3 border border-green-600 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-base min-w-0 focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <div className="flex items-stretch gap-2 shrink-0">
                        <button
                          onClick={confirmRename}
                          className="flex-1 px-3 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center transition-colors duration-200"
                          aria-label="Umbenennen bestätigen"
                          title="Umbenennen bestätigen"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </button>
                        <button
                          onClick={cancelRename}
                          className="flex-1 px-3 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors duration-200"
                          aria-label="Abbrechen"
                          title="Abbrechen"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <TemplateCombobox
                        templates={displayedTemplates}
                        value={selectedTemplateId}
                        onChange={(id) => { setSelectedTemplateId(id); if (id) loadFromTemplate(id); }}
                      />
                      <div className="flex items-stretch gap-2 shrink-0">
                        <button
                          onClick={resetEditorToDefaults}
                          className="flex-1 sm:flex-none px-3 py-3 flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors duration-200"
                          aria-label="Formular leeren"
                          title="Formular leeren"
                        >
                          <EraserIcon />
                        </button>
                        <button
                          onClick={cycleTemplateSort}
                          data-keep-combobox-open
                          aria-pressed={templateSort !== 'none'}
                          className={`flex-1 sm:flex-none px-3 py-3 flex items-center justify-center rounded-lg border transition-colors duration-200 ${templateSort !== 'none'
                            ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
                          aria-label="Vorlagen sortieren"
                          title={sortTitle}
                        >
                          {renderSortIcon()}
                        </button>
                        <button
                          onClick={startRename}
                          disabled={!selectedTemplateId}
                          className="flex-1 sm:flex-none px-3 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                          aria-label="Ausgewählte Vorlage umbenennen"
                          title="Ausgewählte Vorlage umbenennen"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button
                          onClick={() => toggleTemplateArchivedById(selectedTemplateId)}
                          disabled={!selectedTemplateId}
                          className="flex-1 sm:flex-none px-3 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                          aria-label={selectedIsArchived ? 'Vorlage wiederherstellen' : 'Vorlage archivieren'}
                          title={selectedIsArchived ? 'Vorlage wiederherstellen' : 'Vorlage archivieren'}
                        >
                          {selectedIsArchived ? <ArchiveRestore className="w-5 h-5" /> : <Archive className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => deleteTemplateById(selectedTemplateId)}
                          disabled={!selectedTemplateId}
                          className="flex-1 sm:flex-none px-3 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                          aria-label="Vorlage löschen"
                          title="Vorlage löschen"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-stretch min-w-0">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Vorlagenname"
                    className="flex-1 p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-l-lg min-w-0 focus:outline-none focus:ring-0 focus:border-green-600"
                  />
                  <button
                    onClick={saveAsTemplate}
                    className="px-6 bg-green-600 text-white rounded-r-lg hover:bg-green-700 flex items-center gap-2 whitespace-nowrap font-semibold shadow-xs"
                    aria-label="Vorlage speichern"
                    title="Aktuellen Stand als wiederverwendbare Vorlage speichern"
                  >
                    <SaveIcon />
                    <span className="text-base">Vorlage speichern</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Version history */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Versionsverlauf</label>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 max-w-lg">
                <select
                  value={selectedVersionId || ""}
                  onChange={(e) => {
                    const id = parseInt(e.target.value);
                    setSelectedVersionId(id);
                    if (id) restoreVersion(id);
                  }}
                  className="flex-1 min-w-0 p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-base"
                >
                  <option value="">Version wiederherstellen...</option>
                  {versions.slice().reverse().map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.data?.title ? `${v.data.title} - ` : ''}{new Date(v.timestamp).toLocaleString('de-DE')}
                    </option>
                  ))}
                </select>
                <div className="flex items-stretch gap-2 shrink-0">
                  <button
                    onClick={() => deleteVersion(selectedVersionId)}
                    disabled={!selectedVersionId}
                    className="px-3 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center min-w-[44px]"
                    aria-label="Version löschen"
                    title="Version löschen"
                  >
                    <TrashIcon />
                  </button>
                  <button
                    onClick={saveVersion}
                    className="flex-1 sm:flex-none px-3 py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center gap-2 whitespace-nowrap font-medium"
                    aria-label="Zwischenspeichern (⌘S)"
                    title="Temporärer Zwischenstand als Version – nichts Permanentes (⌘S)"
                  >
                    <SaveIcon />
                    <span className="text-xs">Zwischenspeichern</span>
                    <span className="text-xs opacity-70">⌘S</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
